import React, { useState } from "react";
import { motion } from "framer-motion";
import FeaturedProject from "../components/Projects/FeaturedProject";
import ProjectCard from "../components/Projects/ProjectCard";

const categories = ["All", "Website", "Server", "AI", "CSS", "App"];

// Projects Data
const projectsData = [
  // Existing projects
  {
    title: "BrainTeaserBot",
    description: "BrainTeaserBot is an AI-driven quiz website built with the MERN stack, featuring the Gemini AI API for user interaction and the Open Trivia DB API for dynamic trivia questions. The project highlights my skills in full-stack development and delivers an engaging learning experience.",
    projectLink: "https://brainteaserbot.netlify.app/",
    githubLink: "https://github.com/Animatedguess/BrainTeaserBot",
    imageAlt: "/Image/BrainTeaserBot.webp",
    category: ["Website","AI"],
    isFeatured: true,
  },
  {
    title: "Weather Website",
    category: ["Website"],
    projectLink: "https://weather-billu.netlify.app/",
    githubLink: "https://github.com/Animatedguess/Weather-Website",
    imageAlt: "/Image/weatherBillu.webp",
    isFeatured: false,
  },
  {
    title: "BrainTeaserBot (Server)",
    category: "Server",
    projectLink: "https://backend-brainteaserbot.onrender.com/",
    githubLink: "https://github.com/Animatedguess/Backend--BrainTeaserBot",
    imageAlt: "/Image/Server.webp",
    isFeatured: false,
  },
  {
    title: "Nike Website clone",
    category: ["website","css"],
    projectLink: "https://nike-website0-clone.netlify.app",
    githubLink: "https://github.com/Animatedguess/Nike-Website-Clone",
    imageAlt: "/Image/Nike-clone.png",
    isFeatured: false,
  },
  {
    title: "Youtube Mini Server",
    category: "Server",
    projectLink: "https://github.com/Animatedguess/DataBaseConnect",
    githubLink: "https://github.com/Animatedguess/DataBaseConnect",
    imageAlt: "/Image/Server.webp",
    isFeatured: false,
  }
];

// Project Page Component
const ProjectPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visibleProjects, setVisibleProjects] = useState(3); // Control how many projects are shown initially

  // Filter projects based on category
  const filteredProjects = projectsData.filter((project) =>
    selectedCategory === "All" ? true : project.category.includes(selectedCategory)
  );

  // Show more projects when button is clicked
  const handleShowMore = () => {
    setVisibleProjects((prevCount) => prevCount + 3);
  };

  return (
    <main className="flex w-full flex-col items-center justify-center dark:text-light text-dark overflow-hidden">
      <div className="z-0 inline-block w-full bg-transparent p-4 sm:p-6 lg:p-8 xl:p-10">
        {/* Title */}
        <motion.div
          className="py-4 sm:py-6 md:py-8 w-full mx-auto flex flex-col items-center justify-center text-center overflow-hidden"
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
        >
          <h1 className="hidden">Project of Kailash Agarwal</h1>
          <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-dark dark:text-light capitalize mb-4 break-words w-full max-w-full">
            <span className="transition-transform transform hover:scale-110 hover:text-[#ff934f] duration-300 ease-in-out whitespace-normal break-words">
              Imagination&nbsp;
            </span>
            <span className="transition-transform transform hover:scale-110 hover:text-[#ff934f] duration-300 ease-in-out whitespace-normal break-words">
              Trumps&nbsp;
            </span>
            <span className="transition-transform transform hover:scale-110 hover:text-[#ff934f] duration-300 ease-in-out whitespace-normal break-words">
              Knowledge!&nbsp;
            </span>
          </h1>
        </motion.div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-6">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-semibold ${selectedCategory === category
                ? "bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] text-white"
                : "bg-gray-200 dark:bg-gray-700 text-dark dark:text-light"
                } hover:bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] hover:text-white transition-colors`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:gap-6 xl:gap-8">
          {filteredProjects.slice(0, visibleProjects).map((project, index) =>
            project.isFeatured ? (
              <motion.div
                key={index}
                className="col-span-12"
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
              >
                <FeaturedProject
                  title={project.title}
                  description={project.description}
                  projectLink={project.projectLink}
                  githubLink={project.githubLink}
                  imageAlt={project.imageAlt}
                />
              </motion.div>
            ) : (
              <motion.div
                key={index}
                className="col-span-12 sm:col-span-6 lg:col-span-6"
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
              >
                <ProjectCard
                  title={project.title}
                  category={project.category}
                  projectLink={project.projectLink}
                  githubLink={project.githubLink}
                  imageAlt={project.imageAlt}
                />
              </motion.div>
            )
          )}
        </div>

        {/* Show More Button */}
        {visibleProjects < filteredProjects.length && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleShowMore}
              className="px-6 py-3 bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] text-white font-bold rounded-full shadow-lg hover:shadow-2xl hover:scale-105 transition-transform"
            >
              Show More
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default ProjectPage;
